import { TokenStorage } from "../auth/TokenStorage";
import { refreshTokens } from '../auth/RefreshTokens';
import { browserFetch } from './BrowserFetch';

/**
 * `fetch` wrapper that attaches the stored access token in the auth-token
 * header (not Authorization — a contract kept from when `/api/` sat behind the
 * nginx Basic Auth gate) and, on a 419, transparently refreshes the token pair
 * once and retries. The backend signals auth failures with 419, not 401: the
 * convention originated to avoid the browser's native Basic Auth dialog and is
 * kept as the refresh-once/else-logout signal (see docs/deployment.md). It carries
 * no tracing — callers that also need trace propagation use `tracedFetch`, which
 * delegates here. Must NOT be used for the login and refresh endpoints themselves.
 */
export const fetchWithRefresh: typeof fetch = async (input, init) => {
  const attempt = (): Promise<Response> => {
    let attemptOutcome: Promise<Response>;
    const token = TokenStorage.getAccessToken();
    if (token === null) {
      attemptOutcome = init === undefined ? browserFetch(input) : browserFetch(input, init);
    } else {
      const headers = new Headers(init === undefined ? undefined : init.headers);
      headers.set('auth-token', token);
      attemptOutcome = browserFetch(input, { ...init, headers });
    }
    return attemptOutcome;
  };

  const response = await attempt();
  let fetchOutcome = response;
  if (response.status === 419 && (await refreshTokens())) {
    fetchOutcome = await attempt();
  }
  return fetchOutcome;
};
